import {
	Button,	
	ListItemText,
	Menu, ListItemIcon
} from "@mui/material";
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useEffect, useState, useRef } from 'react';
import { ColDef, RowSelectionModule, ColumnApiModule } from "ag-grid-community";
import { useTheme } from '@mui/material/styles';
import { Box, Stack, TextField, Pagination, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import type { RowSelectionOptions } from "ag-grid-community";
import { ModuleRegistry, TooltipModule } from "ag-grid-community";
import PageLoading from "./PageLoading";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CheckIcon from '@mui/icons-material/Check';

// Register the RowSelectionModule. ColumnApiModule is required so event.api.getColumnState()
// works in handleSortChanged — without it AG Grid v33 returns undefined and sorting throws.
ModuleRegistry.registerModules([RowSelectionModule, TooltipModule, ColumnApiModule]);

interface PaginationData {
	data: any[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}

interface PaginatedServerSideDataTableProps {
	columns: ColDef[];
	paginationData?: PaginationData;
	actions?: JSX.Element;
	noRowsText?: string;
	onSelectionChange?: (selected: any[]) => void;
	rowSelection?: RowSelectionOptions;
	loading?: boolean;
	getRowHeight?: (params: any) => number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	onSearchChange: (search: string) => void;
	onSortChange?: (field: string, direction: "asc" | "desc" | null) => void;
	searchPlaceholder?: string;
	columnLabels?: Record<string, string>,
	showDomainSearch?: boolean
	onDomainSearchChange?: (value: string) => void
	domainSearchPlaceholder?: string
}

export default function PaginatedServerSideDataTable(props: PaginatedServerSideDataTableProps) {
	const muiTheme = useTheme();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const openMenu = Boolean(anchorEl);
	const pageSizes = [10, 50, 100, 500, 1000, 1500, 2000];

	// For switching themes.
	const [gridTheme, setGridTheme] = useState('ag-theme-alpine');

	// For filtering rows.
	const [filterText, setFilterText] = useState("");
	const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
	const [domainFilterText, setDomainFilterText] = useState("");
	const domainDebounceRef = useRef<NodeJS.Timeout | null>(null);

	const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(
			props.columns.map(col => col.field || col.colId || "")
		);

	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	const toggleableColumns = useMemo(() => {
		return props.columns.filter(col => {
			const id = col.field || col.colId || "";
			return !!(props.columnLabels && id in props.columnLabels);
		});
	}, [props.columns, props.columnLabels]);

    const displayedColumns = useMemo(() => {
        return props.columns.filter(col => 
            visibleColumnIds.includes(col.field || col.colId || "")
        );
    }, [props.columns, visibleColumnIds]);

    const handleToggle = (id: string) => {
        setVisibleColumnIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

	const handleDomainSearchChange = (value: string) => {
		setDomainFilterText(value);

		if (domainDebounceRef.current) {
			clearTimeout(domainDebounceRef.current);
		}

		domainDebounceRef.current = setTimeout(() => {
			props.onDomainSearchChange?.(value);
		}, 600);
	};
	
	// Inside PaginatedServerSideDataTable.tsx
	const defaultColDef = useMemo(() => ({
		// Set a minimum width for ALL columns so they never squeeze smaller than 120px
		minWidth: 120, 
		flex: 1,
		resizable: true,
		...(props.onSortChange ? { comparator: () => 0 } : {}),
	}), [props.onSortChange]);

	// Forward AG Grid's single-column sort model to the server. No-op when onSortChange is absent.
	const handleSortChanged = (event: any) => {
		const sorted = event.api.getColumnState().find((c: any) => c.sort);
		props.onSortChange?.(sorted?.colId ?? "", sorted?.sort ?? null);
	};

	// Update AG Grid theme when MUI theme changes
	useEffect(() => {
		setGridTheme(muiTheme.palette.mode === 'dark'
			? 'ag-theme-alpine-dark'
			: 'ag-theme-alpine');
	}, [muiTheme.palette.mode]);
	
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			if (domainDebounceRef.current) {
				clearTimeout(domainDebounceRef.current);
			}
		};
	}, []);

	const handleSearchChange = (value: string) => {
        setFilterText(value);

        // Clear existing timer if user is still typing
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set a new timer
        debounceTimerRef.current = setTimeout(() => {
            props.onSearchChange(value);
        }, 600);
    };
    
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

	const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
		props.onPageChange(page);
	};

	const handlePageSizeChange = (event: any) => {
		props.onPageSizeChange(event.target.value as number);
	};

	// Provide default values when paginationData is undefined
	const data = props.paginationData?.data ?? [];
	const totalCount = props.paginationData?.total_count ?? 0;
	const totalPages = props.paginationData?.total_pages ?? 1;
	const currentPage = props.paginationData?.current_page ?? 1;
	const pageSize = props.paginationData?.page_size ?? 50;

	return (
		<Box
			className={gridTheme}
			sx={{
				height: "100%",
				width: '100%',
				'&.ag-theme-alpine': {
					'--ag-foreground-color': muiTheme.palette.text.primary,
					'--ag-background-color': muiTheme.palette.background.paper,
					'--ag-header-foreground-color': muiTheme.palette.text.primary,
					'--ag-header-background-color': muiTheme.palette.background.default,
					'--ag-row-hover-color': muiTheme.palette.action.hover,
				},
				'&.ag-theme-alpine-dark': {
					'--ag-foreground-color': muiTheme.palette.text.primary,
					'--ag-background-color': muiTheme.palette.background.paper,
					'--ag-header-foreground-color': muiTheme.palette.text.primary,
					'--ag-header-background-color': muiTheme.palette.background.default,
					'--ag-row-hover-color': muiTheme.palette.action.hover,
				}
			}}
		>
			<Stack direction={"column"} spacing={2} sx={{ height: "100%" }}>				
					{/* Replace the header control Box in PaginatedServerSideDataTable.tsx with this */}
					<Box 
					display="flex" 
					flexDirection={{ xs: "column", md: "row" }} 
					justifyContent="space-between" 
					alignItems={{ xs: "stretch", md: "center" }}
					gap={2}
				>
					<Stack 
						direction={{ xs: "column", sm: "row" }} 
						spacing={2} 
						alignItems={{ xs: "stretch", sm: "center" }}
						sx={{ width: { xs: "100%", md: "auto" } }}
					>
						<TextField
							placeholder={props.searchPlaceholder || "Search in table..."}
							value={filterText}
							size="small"
							sx={{ width: { xs: "100%", sm: "250px" } }}
							onChange={e => handleSearchChange(e.target.value)}
						/>
						
						{props.showDomainSearch && (
							<TextField
								placeholder={props.domainSearchPlaceholder || "Search by domain..."}
								value={domainFilterText}
								size="small"
								sx={{ width: { xs: "100%", sm: "250px" } }}
								onChange={(e) => handleDomainSearchChange(e.target.value)}
							/>
						)}
						
						{props.columnLabels && (
							<>
								<Button
									variant="outlined"
									size="small"
									startIcon={<ViewColumnIcon />}
									onClick={(e) => setAnchorEl(e.currentTarget)}
									sx={{ 
										textTransform: 'none', 
										color: 'text.primary', 
										borderColor: 'divider',
										height: '40px'
									}}
								>
									Customize Columns
								</Button>
								<Menu
									anchorEl={anchorEl}
									open={openMenu}
									onClose={() => setAnchorEl(null)}
									PaperProps={{ sx: { width: 220, maxHeight: 400, mt: 1, boxShadow: 3 } }}
								>
									<Typography variant="overline" sx={{ px: 2, fontWeight: 'bold', color: 'text.secondary' }}>
										Toggle Columns
									</Typography>
																		
									{toggleableColumns.map((col) => {
										const id = col.field || col.colId || "";
										const label = props.columnLabels?.[id] || col.headerName;
										const isVisible = visibleColumnIds.includes(id);

										return (
											<MenuItem key={id} onClick={() => handleToggle(id)} sx={{ py: 0.5 }}>
												<ListItemIcon sx={{ minWidth: '32px !important' }}>
													{isVisible && <CheckIcon fontSize="small" sx={{ fontSize: 16 }} />}
												</ListItemIcon>
												<ListItemText 
													primary={label} 
													primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }} 
												/>
											</MenuItem>
										);
									})}
								</Menu>
							</>
						)}
					</Stack>

					<Box sx={{ alignSelf: { xs: "flex-start", md: "center" } }}>
						{props.actions}
					</Box>
				</Box>
				<Box sx={{ flexGrow: 1, minHeight: 0 }}>
					<AgGridReact
						loading={props.loading ?? false}
						rowData={data}
						columnDefs={displayedColumns}
						defaultColDef={defaultColDef}
						tooltipShowDelay={0}
						tooltipHideDelay={2000}
						pagination={false}
						enableCellTextSelection={true}
						ensureDomOrder={true}
						overlayNoRowsTemplate={props.noRowsText || "No Rows Available"}
						loadingOverlayComponent={PageLoading}
						rowSelection={props.rowSelection}
						onSortChanged={handleSortChanged}
						onSelectionChanged={(event) => {
							const selectedRows = event.api.getSelectedRows();
							props.onSelectionChange?.(selectedRows);
						}}
						getRowHeight={props.getRowHeight}
					/>
				</Box>
				<Box 
						display="flex" 
						flexDirection={{ xs: "column", sm: "row" }} 
						justifyContent="space-between" 
						alignItems="center"
						gap={2}
						sx={{ pt: 1 }}
					>
					<Box display={"flex"} flexDirection={"row"} alignItems={"center"} gap={2}>
						<FormControl size="small" sx={{ minWidth: 120 }}>
							<InputLabel>Rows per page</InputLabel>
							<Select
								value={pageSize}
								label="Rows per page"
								onChange={handlePageSizeChange}
							>
								{pageSizes.map(size => (
									<MenuItem key={size} value={size}>{size}</MenuItem>
								))}
							</Select>
						</FormControl>
						<Typography variant="body2" color="text.secondary">
							Showing {data.length === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}
							{' '}-{' '}
							{Math.min(currentPage * pageSize, totalCount)}
							{' '}of{' '}
							{totalCount}
						</Typography>
					</Box>
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={handlePageChange}
						color="primary"
						showFirstButton
						showLastButton
					/>
				</Box>
			</Stack>
		</Box>
	)
}

